import { useCallback, useEffect, useMemo, useRef } from "react";

import { format } from "date-fns";
import { useDispatch } from "react-redux";

import { isAssemblyAIScribe } from "@/constants/live-transcription";
import { startTranscriptionSummaryTask } from "@/redux/transcription-summary";
import {
  releaseRecordingFor,
  submissionForSession,
} from "@/services/recordingOwnership";
import type { store as appStore } from "@/store";
import { showError } from "@/utils/notification";

import { AssemblyAITranscriber } from "@components/AssemblyAITranscriber";
import { Transcriber, type TranscriberRef } from "@components/Transcriber";
import type { TextInsertionOptions } from "@components/Transcriber/hooks";
import type { LiveTranscriptTurn } from "@components/Transcriber/segments";

import { formatDuration } from "@pages/ReportStudio/AudioTranscriber/components/ScribeSession";
import type { SpeakerHint } from "@pages/ReportStudio/AudioTranscriber/components/SpeakerChecklist";
import { toLiveFeedSegments } from "@pages/ReportStudio/AudioTranscriber/components/live-feed-segments";
import { buildSummarizeFormData } from "@pages/ReportStudio/AudioTranscriber/components/summarize-form-data";
import {
  formatRecoveredFeed,
  persistLiveFeedForActiveSession,
} from "@pages/ReportStudio/AudioTranscriber/hooks/assemblyAIRecovery";
import { useCallAudioShare } from "@pages/ReportStudio/AudioTranscriber/hooks/useCallAudioShare";
import { useRecordingSession } from "@pages/ReportStudio/AudioTranscriber/hooks/useRecordingSession";
import { joinWebmRecordings } from "@pages/ReportStudio/AudioTranscriber/hooks/webmJoin";

import { downloadBlob } from "@utils/downloadBlob";

import { IDLE_SUMMARIZE, useRecordingDock } from "./store/useRecordingDock";
import { useNotesAutosave } from "./useNotesAutosave";

const ScribeTranscriber = isAssemblyAIScribe() ? AssemblyAITranscriber : Transcriber;

// Throttle for saving the in-progress line, so a refresh loses at most ~1s of it.
const PARTIAL_PERSIST_INTERVAL_MS = 1000;
// Trailing throttle for turn-driven snapshots; each rewrites the whole segment.
const TURNS_PERSIST_INTERVAL_MS = 3000;

const sameTurns = (a: LiveTranscriptTurn[], b: LiveTranscriptTurn[]) =>
  a.length === b.length && a.every((turn, i) => turn === b[i]);

/** Everything needed to (re)send a stopped session's summarize request. */
type PendingSummarize = {
  // Null until the case details load; a retry picks them up from the store.
  caseNumber: string | null;
  examineeName: string | null;
  audioBlob: Blob | null;
  transcript: string;
  speakerHints: SpeakerHint[];
  stoppedAt: Date;
  /** The recording's saved copy, cleared once this request is accepted; null when none was kept. */
  savedSessionId: string | null;
};

// Case number and time only — the examinee's name (PHI) stays out of the file name.
const recordingFileName = ({ caseNumber, audioBlob, stoppedAt }: PendingSummarize) =>
  `assessment-recording-${caseNumber ? `${caseNumber}-` : ""}${format(
    stoppedAt,
    "yyyyMMdd-HHmm"
  )}.${audioBlob?.type.includes("mp4") ? "mp4" : "webm"}`;

/**
 * Root-mounted owner of the live recording session. The live
 * transcriber (Azure or AssemblyAI, per the flag above) + `useRecordingSession` lifecycle live here for the whole app
 * lifetime, so navigating between case tabs never unmounts them; the session is
 * published to the store and the scribe page / floating dock read it. The
 * summarize-on-stop also fires here, so finishing from the dock (scribe tab
 * unmounted) still works.
 */
export const RecordingHost = () => {
  const dispatch = useDispatch<typeof appStore.dispatch>();

  const hostRef = useRef<TranscriberRef>(null);
  const registerTranscriberRef = useRecordingDock((s) => s.registerTranscriberRef);
  const registerSession = useRecordingDock((s) => s.registerSession);
  const endSession = useRecordingDock((s) => s.endSession);
  const setStatus = useRecordingDock((s) => s.setStatus);
  const startClock = useRecordingDock((s) => s.startClock);
  const pauseClock = useRecordingDock((s) => s.pauseClock);
  const registerControls = useRecordingDock((s) => s.registerControls);
  const registerCallAudioShare = useRecordingDock((s) => s.registerCallAudioShare);
  const setSummarize = useRecordingDock((s) => s.setSummarize);

  useEffect(() => {
    registerTranscriberRef(hostRef);
    return () => registerTranscriberRef(null);
  }, [registerTranscriberRef]);

  const latestPartialRef = useRef("");
  const lastPartialPersistAtRef = useRef(0);
  const turnsPersistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Re-mapping every turn is the costly part, so rows are recomputed only when a turn changed.
  const feedRowsCacheRef = useRef<{
    turns: LiveTranscriptTurn[];
    rows: ReturnType<typeof toLiveFeedSegments>;
  }>({ turns: [], rows: [] });

  // Committed rows plus the in-progress line as a provisional row; the next snapshot replaces it.
  const persistFeedSnapshot = useCallback((partial: string) => {
    if (!isAssemblyAIScribe()) return;
    const turns = hostRef.current?.getTurns() ?? [];
    if (!sameTurns(feedRowsCacheRef.current.turns, turns)) {
      feedRowsCacheRef.current = { turns, rows: toLiveFeedSegments(turns) };
    }
    const { rows } = feedRowsCacheRef.current;
    const elapsedMs = useRecordingDock.getState().elapsedMs();
    const text = partial.trim();
    void persistLiveFeedForActiveSession(
      text ? [...rows, { text, timestamp: formatDuration(elapsedMs) }] : rows,
      elapsedMs
    );
  }, []);

  const flushFeedSnapshot = useCallback(() => {
    if (turnsPersistTimerRef.current) {
      clearTimeout(turnsPersistTimerRef.current);
      turnsPersistTimerRef.current = null;
    }
    persistFeedSnapshot(latestPartialRef.current);
  }, [persistFeedSnapshot]);

  useEffect(() => {
    if (!isAssemblyAIScribe()) return;
    // Best-effort: the IndexedDB write may not finish before the page goes away.
    const handlePageHide = () => {
      if (turnsPersistTimerRef.current || latestPartialRef.current.trim())
        flushFeedSnapshot();
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      if (turnsPersistTimerRef.current) clearTimeout(turnsPersistTimerRef.current);
    };
  }, [flushFeedSnapshot]);

  // Telehealth call-tab audio share (MDRS-268) — owned here so it survives
  // scribe-tab navigation like the rest of the session, and so the consent
  // modal (TranscribeAndUploadButtons) and the live view (AudioTranscript)
  // read/drive the SAME share instance instead of independent ones.
  const callAudioShare = useCallAudioShare();
  useEffect(() => {
    registerCallAudioShare(callAudioShare);
  }, [callAudioShare, registerCallAudioShare]);

  useNotesAutosave();

  // MDRS-221 UAT-04: native browser guard so closing/refreshing the tab (or
  // logging out through the browser chrome) while a session is live warns
  // before discarding it — and, MDRS-551, while its recording is unsent, since
  // the in-memory copy is the only one when the browser couldn't save it. Reads
  // the store imperatively at fire time — this effect is only ever set up once,
  // so a captured `isActive` would go stale.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const { isActive, summarize } = useRecordingDock.getState();
      if (!isActive() && summarize.status === "idle") return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // The stopped session's summarize request, held until the server accepts it
  // so a failure can be retried or its audio downloaded (MDRS-551).
  const pendingSummarizeRef = useRef<PendingSummarize | null>(null);
  // Bumped whenever that request stops being the one on screen — a new recording
  // starts (another case can record while this upload is still open), the
  // session ends or is discarded, or this host unmounts — so a late result
  // never updates the UI of whatever came next.
  const summarizeEpochRef = useRef(0);
  const dropPendingSummarize = useCallback(() => {
    summarizeEpochRef.current += 1;
    pendingSummarizeRef.current = null;
  }, []);

  // The saved recording is cleared only once the server accepts the request —
  // clearing it at Stop lost the assessment whenever the upload failed.
  const sendPendingSummarize = useCallback(
    async (status: "sending" | "retrying") => {
      const pending = pendingSummarizeRef.current;
      if (!pending) return;
      const epoch = summarizeEpochRef.current;
      const isCurrent = () => summarizeEpochRef.current === epoch;
      const hasAudio = !!pending.audioBlob;
      // The examinee name can load after Stop (the case query resolves late).
      const dock = useRecordingDock.getState();
      pending.caseNumber ??= dock.caseNumber;
      if (dock.caseNumber === pending.caseNumber) {
        pending.examineeName ??= dock.examineeName;
      }
      const { caseNumber, examineeName } = pending;
      if (!caseNumber || !examineeName) {
        // Don't log the examinee name (PHI).
        console.warn(
          "[RecordingHost] Missing case_number or examinee_name — can't send the recording yet.",
          { hasCaseNumber: !!caseNumber, hasExamineeName: !!examineeName }
        );
        showError(
          "Couldn't send the recording: the case details haven't loaded yet. Try again in a moment, or download the audio.",
          dispatch
        );
        if (isCurrent()) setSummarize({ status: "failed", hasAudio });
        return;
      }
      setSummarize({ status, hasAudio });
      const submissionId = submissionForSession(pending.savedSessionId);
      try {
        await dispatch(
          startTranscriptionSummaryTask(
            buildSummarizeFormData({
              submissionId,
              caseNumber,
              examineeName,
              audioBlob: pending.audioBlob,
              fallbackTranscript: pending.transcript,
              speakerHints: pending.speakerHints,
            })
          )
        );
      } catch {
        // The thunk already showed its error toast. A stale request's audio stays
        // saved for the recovery prompt.
        if (isCurrent()) setSummarize({ status: "failed", hasAudio });
        return;
      }
      // The server has this recording now, so its own saved copy goes — even if
      // a newer session has taken over. Cleared before the × comes back, so
      // dismissing can't surface it as "interrupted".
      if (pending.savedSessionId) {
        await useRecordingDock
          .getState()
          .session?.clearSavedRecording(pending.savedSessionId);
      } else {
        // No saved copy to clear, so release the tab lock here.
        releaseRecordingFor(submissionId);
      }
      if (isCurrent()) {
        pendingSummarizeRef.current = null;
        setSummarize(IDLE_SUMMARIZE);
      }
    },
    [dispatch, setSummarize]
  );

  // A request still open when this host unmounts keeps running, but its result
  // no longer drives the UI — a remount drops the stopped view too — so don't
  // leave a stale unsent state behind. The saved recording stays for the
  // recovery prompt.
  useEffect(() => {
    setSummarize(IDLE_SUMMARIZE);
    return dropPendingSummarize;
  }, [setSummarize, dropPendingSummarize]);

  // A new recording takes over the UI; an earlier upload that's still open
  // finishes in the background (see `sendPendingSummarize`).
  const handleRecordingSessionStart = useCallback(() => {
    dropPendingSummarize();
    setSummarize(IDLE_SUMMARIZE);
  }, [dropPendingSummarize, setSummarize]);

  const downloadRecording = useCallback(() => {
    const pending = pendingSummarizeRef.current;
    if (pending?.audioBlob) {
      downloadBlob(pending.audioBlob, recordingFileName(pending));
    }
  }, []);

  // Dispatch the diarized summarize on session end. Reads the owning
  // case/examinee + transcript from the store/host, so it works off-tab.
  const handleRecordingSessionEnd = useCallback(
    (
      audioBlob: Blob | null,
      speakerHints: SpeakerHint[],
      savedSessionId: string | null
    ) => {
      const { caseNumber, examineeName, priorRows, resumed } =
        useRecordingDock.getState();
      // A resumed recording's earlier segments come first.
      const transcript = [
        priorRows.length > 0 ? formatRecoveredFeed(priorRows) : "",
        hostRef.current?.getFullTranscript()?.trim() ?? "",
      ]
        .filter(Boolean)
        .join("\n");

      if (!audioBlob && !transcript) {
        console.warn(
          "[RecordingHost] No audio blob and no transcript on session end — skipping summarize."
        );
        return;
      }
      // Missing case details are handled by the send, which shows the recording
      // as not sent so it can be retried once they load.

      // Marked sending before any join so the session can't be dismissed mid-send.
      setSummarize({ status: "sending", hasAudio: !!audioBlob });
      const epoch = summarizeEpochRef.current;
      const stoppedAt = new Date();
      const send = (blob: Blob | null) => {
        // Something newer took over during the join; the saved recording stays
        // for the recovery prompt.
        if (summarizeEpochRef.current !== epoch) return;
        pendingSummarizeRef.current = {
          caseNumber,
          examineeName,
          audioBlob: blob,
          transcript,
          speakerHints,
          stoppedAt,
          savedSessionId,
        };
        void sendPendingSummarize("sending");
      };
      // A resumed recording is several WebM files back to back; join them into one timeline.
      if (isAssemblyAIScribe() && audioBlob && resumed) {
        void joinWebmRecordings(audioBlob).then(send);
        return;
      }
      send(audioBlob);
    },
    [setSummarize, sendPendingSummarize]
  );

  const stopRecordingRef = useRef<(() => void) | null>(null);
  // Tags the audio buffer with the case that owns the session (diagnostic only —
  // cross-session isolation comes from session-id ownership). Read imperatively
  // at claim time: the scribe page calls `beginSession` and `start` in the same
  // tick, so a rendered value would always be stale.
  const getOwningCaseNumber = useCallback(
    () => useRecordingDock.getState().caseNumber,
    []
  );
  const getOwningExamineeName = useCallback(
    () => useRecordingDock.getState().examineeName,
    []
  );
  const session = useRecordingSession({
    transcriberRef: hostRef,
    onRecordingSessionStart: handleRecordingSessionStart,
    onRecordingSessionEnd: handleRecordingSessionEnd,
    stopRecordingRef,
    getOwningCaseNumber,
    getOwningExamineeName,
  });

  // Publish the live session + its imperative controls to the store so the
  // scribe page (live view) and the floating dock can both drive it.
  useEffect(() => {
    registerSession(session);
    registerControls({
      pause: () => session.pause(),
      resume: () => session.resume(),
      stop: () => session.stop(),
      retrySummarize: () => sendPendingSummarize("retrying"),
      downloadRecording,
      discardRecording: () => {
        dropPendingSummarize();
        setSummarize(IDLE_SUMMARIZE);
        // Cancelling a stopped session deletes its saved recording.
        session.cancel();
      },
    });
  }, [
    session,
    registerSession,
    registerControls,
    sendPendingSummarize,
    downloadRecording,
    dropPendingSummarize,
    setSummarize,
  ]);

  // Project the session status → store (case ownership + clock lifecycle) so
  // the dock renders correctly. The owning case is claimed by the
  // scribe page via beginSession at start; here we just track status + clock.
  const status = session.status;
  // A remount renders once with the initial `idle` before the hook's adoption
  // effect restores the live status, so treating that first `idle` as "session
  // over" would call endSession() and wipe the owning case, examinee and clock
  // out from under a recording that is still running (MDRS-233). Only an idle
  // that follows a non-idle status is a real end.
  const sawNonIdleRef = useRef(false);
  useEffect(() => {
    if (status === "idle") {
      if (sawNonIdleRef.current) {
        sawNonIdleRef.current = false;
        // A request still in flight belongs to the session that just ended.
        dropPendingSummarize();
        endSession();
      }
      return;
    }
    sawNonIdleRef.current = true;
    setStatus(status);
    if (status === "recording") startClock();
    else if (status === "paused" || status === "stopped") pauseClock();
  }, [status, setStatus, startClock, pauseClock, endSession, dropPendingSummarize]);

  // Stable delegating callback — reads the currently-registered live callback
  // imperatively so it never churns the Transcriber's internal callbackRef.
  const callback = useMemo(
    () => ({
      onFocus: () => useRecordingDock.getState().liveCallback?.onFocus?.(),
      onEnd: () => useRecordingDock.getState().liveCallback?.onEnd?.(),
      // Save what the feed has, then leave the recording resumable instead of capturing silence.
      onFatalError: () => {
        if (!isAssemblyAIScribe()) return;
        flushFeedSnapshot();
        useRecordingDock.getState().session?.suspend();
      },
      onTextInsert: (text: string, options: TextInsertionOptions) =>
        useRecordingDock.getState().liveCallback?.onTextInsert(text, options),
      onPartialUpdate: (partial: string) => {
        latestPartialRef.current = partial;
        const now = Date.now();
        if (
          partial.trim() &&
          now - lastPartialPersistAtRef.current >= PARTIAL_PERSIST_INTERVAL_MS
        ) {
          lastPartialPersistAtRef.current = now;
          persistFeedSnapshot(partial);
        }
        useRecordingDock.getState().liveCallback?.onPartialUpdate?.(partial);
      },
      onTurnsUpdate: (turns: LiveTranscriptTurn[]) => {
        // Persisted at the host so crash recovery still works when the scribe tab is closed.
        if (isAssemblyAIScribe() && !turnsPersistTimerRef.current) {
          turnsPersistTimerRef.current = setTimeout(
            flushFeedSnapshot,
            TURNS_PERSIST_INTERVAL_MS
          );
        }
        useRecordingDock.getState().liveCallback?.onTurnsUpdate?.(turns);
      },
    }),
    [persistFeedSnapshot, flushFeedSnapshot]
  );

  return (
    <div className="hidden" aria-hidden="true">
      <ScribeTranscriber ref={hostRef} callback={callback} isPersistentSession />
    </div>
  );
};
